import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_KEYS = [
  import.meta.env.VITE_YOUTUBE_API_KEY,
  import.meta.env.VITE_YOUTUBE_API_KEY_2,
].filter(Boolean);
const CHANNEL_ID = import.meta.env.VITE_YOUTUBE_CHANNEL_ID;
const CACHE_KEY = 'agility_latest_video_v1';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

function decodeHtml(str) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

function getRelativeTime(dateString) {
  const published = new Date(dateString);
  const now = new Date();
  const diffMs = now - published;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears >= 1) return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
  if (diffMonths >= 1) return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  if (diffWeeks >= 1) return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`;
  if (diffDays >= 1) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  if (diffHours >= 1) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  if (diffMins >= 1) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  return 'Just now';
}

const LatestVideo = () => {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [thumbHovered, setThumbHovered] = useState(false);

  useEffect(() => {
    const isHeadless = navigator.webdriver || /HeadlessChrome/.test(navigator.userAgent);
    if (isHeadless || !API_KEYS.length || !CHANNEL_ID) {
      setError(true);
      setLoading(false);
      return;
    }

    // Return cached result if fresh
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL && data?.videoId) {
          setVideo(data);
          setLoading(false);
          return;
        }
      }
    } catch (_) {}

    const durationSeconds = (iso) => {
      const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (!m) return 0;
      return (parseInt(m[1] || 0) * 3600) + (parseInt(m[2] || 0) * 60) + parseInt(m[3] || 0);
    };

    // Try each API key in order, falling back if one hits quota/error
    const tryFetch = (keyIndex) => {
      if (keyIndex >= API_KEYS.length) {
        setError(true);
        setLoading(false);
        return;
      }
      const key = API_KEYS[keyIndex];

      // videoDuration=medium pre-filters to 4–20 min server-side, blocking most Shorts
      const searchUrl =
        `https://www.googleapis.com/youtube/v3/search` +
        `?part=snippet&channelId=${CHANNEL_ID}&maxResults=25&order=date&type=video` +
        `&videoDuration=medium&key=${key}`;

      fetch(searchUrl)
        .then((res) => {
          // 403 = quota exceeded or key invalid — try next key
          if (res.status === 403) throw new Error('quota');
          if (!res.ok) throw new Error('API error');
          return res.json();
        })
        .then((data) => {
          const items = data?.items;
          if (!items?.length) throw new Error('No items');

          const ids = items.map((i) => i.id.videoId).join(',');
          const detailsUrl =
            `https://www.googleapis.com/youtube/v3/videos` +
            `?part=contentDetails&id=${ids}&key=${key}`;

          return fetch(detailsUrl)
            .then((r) => {
              if (r.status === 403) throw new Error('quota');
              if (!r.ok) throw new Error('Details error');
              return r.json();
            })
            .then((details) => {
              const durationMap = {};
              (details.items || []).forEach((d) => {
                durationMap[d.id] = durationSeconds(d.contentDetails.duration);
              });

              const isShort = (i) => {
                if ((durationMap[i.id.videoId] || 0) <= 180) return true;
                const text = (i.snippet.title + ' ' + i.snippet.description).toLowerCase();
                return text.includes('#shorts') || text.includes('#short');
              };
              const item = items.find((i) => !isShort(i));
              if (!item) throw new Error('No non-short videos');

              const { snippet } = item;
              const videoData = {
                videoId: item.id.videoId,
                title: decodeHtml(snippet.title),
                description: decodeHtml(snippet.description),
                publishedAt: snippet.publishedAt,
                thumbnail: snippet.thumbnails?.high?.url || snippet.thumbnails?.medium?.url,
              };

              // Cache for 24 hours to avoid quota exhaustion
              try {
                localStorage.setItem(CACHE_KEY, JSON.stringify({ data: videoData, timestamp: Date.now() }));
              } catch (_) {}

              setVideo(videoData);
              setLoading(false);
            });
        })
        .catch((err) => {
          // On quota error try the next key, otherwise show fallback
          if (err.message === 'quota') {
            tryFetch(keyIndex + 1);
          } else {
            setError(true);
            setLoading(false);
          }
        });
    };

    tryFetch(0);
  }, []);

  const videoSchema = video
    ? JSON.stringify({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": video.title,
        "description": video.description.slice(0, 250) || video.title,
        "thumbnailUrl": video.thumbnail,
        "uploadDate": video.publishedAt,
        "embedUrl": `https://www.youtube.com/embed/${video.videoId}`,
        "url": `https://www.youtube.com/watch?v=${video.videoId}`,
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        }
      })
    : null;

  return (
    <section style={{ backgroundColor: '#0a0a0a' }} className="py-20">
      {videoSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: videoSchema }} />
      )}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section heading */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Fresh From Agility
          </h2>
          <div
            style={{ backgroundColor: '#2563eb', height: '3px', width: '56px' }}
            className="rounded-full"
          />
        </div>

        {loading ? (
          <div className="flex flex-col md:flex-row gap-10 animate-pulse">
            <div
              className="w-full md:w-1/2 rounded-xl"
              style={{ backgroundColor: '#1a1a1a', aspectRatio: '16/9' }}
            />
            <div className="w-full md:w-1/2 flex flex-col gap-4 justify-center">
              <div className="h-7 rounded" style={{ backgroundColor: '#1a1a1a', width: '80%' }} />
              <div className="h-7 rounded" style={{ backgroundColor: '#1a1a1a', width: '60%' }} />
              <div className="h-4 rounded mt-2" style={{ backgroundColor: '#1a1a1a', width: '100%' }} />
              <div className="h-4 rounded" style={{ backgroundColor: '#1a1a1a', width: '90%' }} />
              <div className="h-4 rounded" style={{ backgroundColor: '#1a1a1a', width: '70%' }} />
              <div className="h-4 rounded mt-2" style={{ backgroundColor: '#1a1a1a', width: '30%' }} />
              <div className="h-11 rounded-full mt-4" style={{ backgroundColor: '#1a1a1a', width: '200px' }} />
            </div>
          </div>
        ) : error ? (
          /* Fallback: channel link when API is unavailable */
          <div className="flex flex-col md:flex-row gap-10 items-center">
            <div
              className="w-full md:w-1/2 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: '#111111', aspectRatio: '16/9' }}
            >
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24" rx="4" fill="#2563eb" />
                <polygon points="9,7 19,12 9,17" fill="white" />
              </svg>
            </div>
            <div className="w-full md:w-1/2 flex flex-col gap-4">
              <h3 className="text-2xl md:text-3xl font-bold text-white leading-snug">
                Watch Our Latest Videos
              </h3>
              <p className="text-gray-400 text-base leading-relaxed">
                Stay up to date with our latest AI insights, tutorials, and thought leadership on YouTube.
              </p>
              <div className="flex flex-wrap gap-3 items-center">
                <a
                  href={`https://www.youtube.com/channel/${CHANNEL_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-semibold text-white transition-opacity duration-200 hover:opacity-80"
                  style={{
                    backgroundColor: '#2563eb',
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    textDecoration: 'none',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                  Visit Our Channel
                </a>
                <a
                  href="/youtube"
                  className="inline-flex items-center gap-2 font-semibold transition-colors duration-200"
                  style={{
                    color: '#2563eb',
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    border: '2px solid #2563eb',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#2563eb';
                  }}
                >
                  See More
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12,5 19,12 12,19" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        ) : video ? (
          <div className="flex flex-col md:flex-row gap-10 items-center">

            {/* LEFT: Thumbnail */}
            <div className="w-full md:w-1/2">
              <a
                href={`https://www.youtube.com/watch?v=${video.videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block rounded-xl overflow-hidden"
                style={{ aspectRatio: '16/9' }}
                onMouseEnter={() => setThumbHovered(true)}
                onMouseLeave={() => setThumbHovered(false)}
              >
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-300"
                  style={{ transform: thumbHovered ? 'scale(1.04)' : 'scale(1)' }}
                />
                <div
                  className="absolute inset-0 transition-opacity duration-300"
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.35)',
                    opacity: thumbHovered ? 1 : 0,
                  }}
                />
                <div
                  className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
                  style={{ opacity: thumbHovered ? 1 : 0 }}
                >
                  <div
                    className="flex items-center justify-center rounded-full"
                    style={{ width: '72px', height: '72px', backgroundColor: '#2563eb' }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                      <polygon points="5,3 19,12 5,21" />
                    </svg>
                  </div>
                </div>
              </a>
            </div>

            {/* RIGHT: Video info */}
            <div className="w-full md:w-1/2 flex flex-col gap-4">
              <h3
                className="text-2xl md:text-3xl font-bold text-white leading-snug"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {video.title}
              </h3>

              <p
                className="text-gray-400 text-base leading-relaxed"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {video.description}
              </p>

              <p className="text-sm" style={{ color: '#9ca3af' }}>
                {getRelativeTime(video.publishedAt)}
              </p>

              <div className="flex flex-wrap gap-3 items-center">
                <a
                  href={`https://www.youtube.com/watch?v=${video.videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-semibold text-white transition-opacity duration-200 hover:opacity-80"
                  style={{
                    backgroundColor: '#2563eb',
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    textDecoration: 'none',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                  Watch on YouTube
                </a>

                <a
                  href="/youtube"
                  className="inline-flex items-center gap-2 font-semibold transition-colors duration-200"
                  style={{
                    color: '#2563eb',
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    border: '2px solid #2563eb',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#2563eb';
                  }}
                >
                  See More
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12,5 19,12 12,19" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default LatestVideo;
